/**
 * Les pastilles des calques masqués (plan hidden-layers-in-editor) : un calque dont la
 * condition de visibilité ne passe pas pour les valeurs de l'éditeur est éclipsé (lib
 * fabric_bindings — ni dessiné, ni atteignable, hors du flux) ; une pastille « œil barré » le
 * signale à sa place, sur une couche HTML posée sur le canvas.
 *
 * Un clic ouvre le panneau du calque masqué : sa condition, « Afficher en transparence »
 * (le calque redevient un fantôme réglable — ou repart), et un bouton par variable de la
 * condition, qui ouvre le menu « Variables du document » sur ce champ : on change la valeur
 * pour que la condition passe.
 *
 * Le markup est au serveur (gabarits de documents/editors/images/_form) : le JS clone, pose
 * des attributs (`data-revealed`, `hidden`) et des positions — jamais de classe.
 *
 *   const badges = new HiddenLayerBadges(editor, { layer, badge, panel, variable }, {
 *     toggle: (layerId, reveal) => …, editVariable: (key) => …,
 *   });
 *   badges.refresh();   // après chaque évaluation des conditions
 */
import type { FabricEditor } from "@frabr/fabric-editor";
import { Point, util, type FabricObject } from "fabric";
import { hiddenLayersOf, type HiddenLayer } from "./fabric_bindings";
import { attachTooltip, openPopover } from "./bubble";

type PopoverElement = HTMLElement & { showPopover(): void; hidePopover(): void };

export interface HiddenLayerBadgeElements {
  /** La couche des pastilles, calée sur le canvas (inset-0, frère du container fabric). */
  layer: HTMLElement;
  /** Le gabarit d'une pastille (un bouton). */
  badge: HTMLTemplateElement;
  /** Le panneau d'un calque masqué (popover natif). */
  panel: PopoverElement;
  /** Le gabarit du bouton d'une variable de la condition. */
  variable: HTMLTemplateElement;
  /** Le libellé de l'infobulle de la pastille. */
  tooltip: string;
}

export interface HiddenLayerBadgeActions {
  toggle: (layerId: string, reveal: boolean) => void;
  editVariable: (key: string) => void;
}

/** Les variables qu'une condition lit, dans l'ordre, sans doublon (`$PROVIDER`, `$PRIX`). */
export function conditionVariables(code: string): string[] {
  return [...new Set([...code.matchAll(/\$([A-Za-z0-9_]+)/g)].map((m) => m[1].toUpperCase()))];
}

/** La place d'une pastille : le coin haut-gauche de la boîte du calque, dans le repère du
 *  canvas (CSS px) ; les pastilles qui tomberaient au même endroit se décalent d'un cran. */
export function badgePositions(
  boxes: { id: string; left: number; top: number }[],
  step = 28,
): Map<string, { x: number; y: number }> {
  const placed: { x: number; y: number }[] = [];
  const out = new Map<string, { x: number; y: number }>();
  boxes.forEach(({ id, left, top }) => {
    let x = left;
    while (placed.some((p) => Math.abs(p.x - x) < step && Math.abs(p.y - top) < step)) x += step;
    placed.push({ x, y: top });
    out.set(id, { x, y: top });
  });
  return out;
}

export class HiddenLayerBadges {
  private badges = new Map<string, { el: HTMLElement; detach: () => void }>();
  private frame = 0;
  private closePanel: (() => void) | null = null;
  private onRender = () => this.schedule();

  constructor(
    private editor: FabricEditor,
    private els: HiddenLayerBadgeElements,
    private actions: HiddenLayerBadgeActions,
  ) {
    editor.canvas.on("after:render", this.onRender);
    editor.onResize(this.onRender);
  }

  /** Les pastilles suivent la dernière évaluation des conditions. */
  refresh(): void {
    const hidden = hiddenLayersOf(this.editor);
    const ids = new Set(hidden.map((h) => h.layerId));
    this.badges.forEach(({ el, detach }, id) => {
      if (ids.has(id)) return;
      detach();
      el.remove();
      this.badges.delete(id);
    });
    hidden.forEach((h) => {
      const badge = this.badges.get(h.layerId) ?? this.create(h.layerId);
      badge.el.toggleAttribute("data-revealed", h.revealed);
    });
    this.place();
  }

  destroy(): void {
    this.editor.canvas.off("after:render", this.onRender);
    cancelAnimationFrame(this.frame);
    this.closePanel?.();
    this.badges.forEach(({ el, detach }) => { detach(); el.remove(); });
    this.badges.clear();
  }

  private schedule(): void {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.place());
  }

  private create(layerId: string) {
    const el = (this.els.badge.content.firstElementChild as HTMLElement).cloneNode(true) as HTMLElement;
    el.dataset.layerId = layerId;
    el.addEventListener("click", (e) => {
      e.stopPropagation();
      this.open(layerId, el);
    });
    // Un clic sur la pastille ne doit pas désélectionner (mousedown->fabric#deselectOutside)
    el.addEventListener("mousedown", (e) => e.stopPropagation());
    this.els.layer.append(el);
    const badge = { el, detach: attachTooltip(el, { text: this.els.tooltip }) };
    this.badges.set(layerId, badge);
    return badge;
  }

  private place(): void {
    const hidden = hiddenLayersOf(this.editor);
    // Le repère de la scène vers celui de l'élément canvas (zoom, plan de travail) : le canvas
    // fabric lui-même, pas son enveloppe DesignCanvas
    const vpt = this.editor.canvas.originalFabricCanvas.viewportTransform;
    const boxes = hidden.map((h) => {
      const obj = h.obj as FabricObject;
      // Un objet éclipsé n'est jamais dessiné : ses coins en cache peuvent dater
      obj.setCoords();
      const rect = obj.getBoundingRect();
      const corner = util.transformPoint(new Point(rect.left, rect.top), vpt);
      return { id: h.layerId, left: corner.x, top: corner.y };
    });
    badgePositions(boxes).forEach(({ x, y }, id) => {
      const el = this.badges.get(id)?.el;
      if (!el) return;
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
    });
  }

  private open(layerId: string, anchor: HTMLElement): void {
    const hidden = hiddenLayersOf(this.editor).find((h) => h.layerId === layerId);
    if (!hidden) return;

    this.fill(hidden);
    this.closePanel?.();
    this.closePanel = openPopover(anchor, this.els.panel, { placement: "bottom-start" });
  }

  private fill(hidden: HiddenLayer): void {
    const panel = this.els.panel;
    panel.querySelector<HTMLElement>("[data-role=condition]")!.textContent = hidden.condition;
    panel.toggleAttribute("data-revealed", hidden.revealed);

    const toggle = panel.querySelector<HTMLButtonElement>("[data-role=toggle]")!;
    toggle.onclick = () => {
      this.closePanel?.();
      this.actions.toggle(hidden.layerId, !hidden.revealed);
    };

    const list = panel.querySelector<HTMLElement>("[data-role=variables]")!;
    list.replaceChildren(...conditionVariables(hidden.condition).map((key) => {
      const button = (this.els.variable.content.firstElementChild as HTMLElement).cloneNode(true) as HTMLElement;
      button.querySelector<HTMLElement>("[data-role=token]")!.textContent = `$${key}`;
      button.addEventListener("click", () => {
        this.closePanel?.();
        this.actions.editVariable(key);
      });
      return button;
    }));
  }
}
